import { useT } from "@transifex/react";
import { UnitCard, UserOwnCard } from "p6m-p6u";
import React, { useEffect, useMemo, useState } from "react";
import { CreationModes } from "../../../constants/CreationFlowConstants";
import { useGeneralContext } from "../../../context/AppGeneralContext";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { sanitizeText } from "../../../helpers/TestCreation";
import { AddCardResults } from "../../../enums/cardSelection";

import Checkbox from "../../checkbox/Checkbox";
import {
    AlreadyUsedTooltip,
    AnswerWrapper,
    CardAlreadyInUseIconWrapper,
    CardWrapper,
    CheckboxWrapper,
    GapSentenceWrapper,
    QuestionAnswerWrapper,
    QuestionWrapper,
    SmallP6Icon,
    TextSpan,
} from "./StyledComponents";

export interface CardListingCardItemProps {
    card: UnitCard | UserOwnCard;
    isChecked: boolean;
    openModalCallback: () => any;
    hideGapSentences?: boolean;
}
const CardListingCardItemRT: React.FC<CardListingCardItemProps> = (props) => {
    const {
        vocabularyDrawerContent,
        addCardToSelectedWords,
        removeCardFromSelectedWords,
        maxAmountOfCardsSelectable,
        openWarningModal,
        previouslyUsedCardsIds,
        previouslyUsedCardsClassName,
    } = useTestCreationContext();
    const { creatingMode } = useGeneralContext();
    const card = props.card;

    const [isChecked, setIsChecked] = useState(false);

    const drawerObjContent = useMemo(
        () => vocabularyDrawerContent[card.id || ""] || {},
        [card.id, vocabularyDrawerContent]
    );

    const t = useT();
    const t_cantAddMoreThan50 = t("Can't add more than {maxAmountOfCardsSelectable} cards", {
        maxAmountOfCardsSelectable,
        _tags: "CreateTest,UnitAndCardsWrapper",
    });

    const t_alreadyAssigned = t("Already assigned to {className}", {
        _tags: "CreateVocabTraining,CardListingCardItemRT",
        className: previouslyUsedCardsClassName,
    });

    const isRecurringTestMode = creatingMode === CreationModes.RECURRING_TASK;

    // A card can be keyed by either uuid (publisher cards) or id (own content) depending on the
    // flow, so match the "previously used" data against both to avoid missing the indicator.
    const cardMatchIds = ["uuid" in card ? card.uuid : undefined, card.id].filter((id): id is string => !!id);
    const isCardInPreviouslyUsed = cardMatchIds.some((id) => previouslyUsedCardsIds.includes(id));

    const hasExercises = !!drawerObjContent.exerciseIds?.length;

    useEffect(() => {
        setIsChecked(!!drawerObjContent.wordContent?.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerObjContent]);

    function toggleCheckedStatus() {
        if (isChecked) {
            if (hasExercises) {
                props.openModalCallback();
            } else {
                removeCardFromSelectedWords(card.id!);
                setIsChecked(false);
            }
            return;
        }

        const addResult = addCardToSelectedWords(card);
        if (addResult === AddCardResults.CAP_REACHED) {
            openWarningModal(<p>{t_cantAddMoreThan50}</p>);
        } else if (addResult === AddCardResults.ADDED) {
            setIsChecked(true);
        }
        // PENDING_CONFIRMATION: the confirmation modal handles the add; the checkbox syncs via effect.
    }

    function getQuestionText(): string {
        if ("question_text" in card && card.question_text) {
            return sanitizeText(card.question_text);
        }
        if ("question" in card) {
            return card.question ?? "";
        }
        return "";
    }

    function getAnswerText(): string {
        if ("answer_text" in card && card.answer_text) {
            return sanitizeText(card.answer_text);
        }
        if ("answer" in card) {
            return card.answer ?? "";
        }
        return "";
    }

    return (
        <CardWrapper data-testid={`vocab-card-${card.id}`}>
            <CheckboxWrapper onClick={toggleCheckedStatus}>
                {hasExercises ? (
                    <SmallP6Icon
                        className={"orange"}
                        name={"in-use"}
                    />
                ) : (
                    <Checkbox
                        isChecked={isChecked}
                        isRecurringTask={isRecurringTestMode}
                    />
                )}
            </CheckboxWrapper>
            <QuestionAnswerWrapper onClick={toggleCheckedStatus}>
                <QuestionWrapper>
                    <TextSpan className={isChecked ? "checked" : ""}>{getQuestionText()}</TextSpan>
                </QuestionWrapper>
                <AnswerWrapper>
                    <TextSpan className={isChecked ? "checked" : ""}>{getAnswerText()}</TextSpan>
                </AnswerWrapper>
                {!props.hideGapSentences && "gap_sentence" in card && card.gap_sentence !== "" && (
                    <GapSentenceWrapper>
                        <TextSpan className={isChecked ? "checked" : ""}>
                            {" "}
                            <b>&#8594;</b> <i>{card.gap_sentence ? sanitizeText(card.gap_sentence) : ""}</i>
                        </TextSpan>
                    </GapSentenceWrapper>
                )}
            </QuestionAnswerWrapper>
            {isRecurringTestMode && (
                <CardAlreadyInUseIconWrapper onClick={(e) => e.stopPropagation()}>
                    {isCardInPreviouslyUsed && (
                        <>
                            {previouslyUsedCardsClassName && (
                                <AlreadyUsedTooltip>{t_alreadyAssigned}</AlreadyUsedTooltip>
                            )}
                            <SmallP6Icon
                                className="grey"
                                name="exercise-ok"
                            />
                        </>
                    )}
                </CardAlreadyInUseIconWrapper>
            )}
        </CardWrapper>
    );
};

export default CardListingCardItemRT;
